import PrimaryButton from '../../../components/buttons/PrimaryButton'
import PasswordComplexityRules from '../../../components/PasswordComplexityRules'
import AuthRecoveryFooter from './components/AuthRecoveryFooter'
import AuthRecoveryTopBar from './components/AuthRecoveryTopBar'
import { PASSWORD_RECOVERY_COPY } from './constants/passwordRecoveryContent'
import { usePasswordResetScreen } from './hooks/usePasswordResetScreen'

function FeedbackBanner({ type = 'error', message }) {
  if (!message) return null
  const className =
    type === 'error' ? 'bg-error/10 text-error' : 'bg-primary/10 text-primary'

  return (
    <div className={`rounded-lg px-4 py-3 text-sm ${className}`} role={type === 'error' ? 'alert' : 'status'}>
      {message}
    </div>
  )
}

export default function PasswordResetScreen() {
  const {
    email,
    mode,
    verifiedOtp,
    currentPassword,
    newPassword,
    confirmPassword,
    complexity,
    isSubmitting,
    error,
    successMessage,
    setNewPassword,
    setConfirmPassword,
    handlePasswordReset,
    handleBackToOtp,
  } = usePasswordResetScreen()

  return (
    <div className="flex min-h-screen flex-col bg-surface text-on-surface">
      <AuthRecoveryTopBar />

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="mx-auto grid w-full max-w-6xl gap-12 lg:grid-cols-12 lg:items-center">
          <section className="space-y-8 lg:col-span-5">
            <h1 className="font-display text-7xl font-extrabold leading-[0.95] tracking-tight text-primary">
              Establish your
              <br />
              Anchor.
            </h1>

            <div className="flex items-start gap-5">
              <div className="mt-3 h-px w-12 bg-outline-variant" />
              <p className="max-w-sm text-lg leading-relaxed text-secondary">
                {PASSWORD_RECOVERY_COPY.resetSubtitle}
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-end justify-between">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  {PASSWORD_RECOVERY_COPY.resetProgressLabel}
                </span>
                <span className="text-xs font-medium text-primary">
                  {PASSWORD_RECOVERY_COPY.resetProgressStep}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                <div className="h-full w-2/3 rounded-full bg-primary" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="rounded-xl bg-surface-container-low p-4">
                <span className="material-symbols-outlined mb-2 text-primary text-[20px]">encrypted</span>
                <p className="text-xs font-bold uppercase tracking-[0.04em]">End-to-End Encryption</p>
              </div>
              <div className="rounded-xl bg-surface-container-low p-4">
                <span className="material-symbols-outlined mb-2 text-primary text-[20px]">gpp_good</span>
                <p className="text-xs font-bold uppercase tracking-[0.04em]">Zero-Trust Architecture</p>
              </div>
            </div>
          </section>

          <section className="mx-auto w-full max-w-md rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-10 shadow-ambient lg:col-span-7">
            <form className="space-y-6" onSubmit={handlePasswordReset}>
              <FeedbackBanner type="error" message={error} />
              <FeedbackBanner type="success" message={successMessage} />

              <div className="space-y-2">
                <label className="block px-1 text-sm font-semibold text-on-surface-variant">
                  {PASSWORD_RECOVERY_COPY.resetCurrentPasswordLabel}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={currentPassword || '........'}
                    readOnly
                    disabled
                    className="h-14 w-full rounded-lg border-0 bg-surface-container-low px-4 pr-12 text-sm text-on-surface outline-none disabled:cursor-not-allowed disabled:opacity-70"
                  />
                  <span className="material-symbols-outlined pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-outline">
                    lock
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block px-1 text-sm font-semibold text-on-surface-variant">
                  {PASSWORD_RECOVERY_COPY.resetNewPasswordLabel}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="........"
                    disabled={isSubmitting}
                    className="h-14 w-full rounded-lg border-0 bg-surface-container-low px-4 pr-12 text-sm text-on-surface outline-none transition-all focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary/35 disabled:cursor-not-allowed disabled:opacity-70"
                  />
                  <span className="material-symbols-outlined pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-outline">
                    key
                  </span>
                </div>
              </div>

              <PasswordComplexityRules complexity={complexity} />

              <div className="space-y-2">
                <label className="block px-1 text-sm font-semibold text-on-surface-variant">
                  {PASSWORD_RECOVERY_COPY.resetConfirmPasswordLabel}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="........"
                    disabled={isSubmitting}
                    className="h-14 w-full rounded-lg border-0 bg-surface-container-low px-4 pr-12 text-sm text-on-surface outline-none transition-all focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary/35 disabled:cursor-not-allowed disabled:opacity-70"
                  />
                  <span className="material-symbols-outlined pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-outline">
                    verified_user
                  </span>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <PrimaryButton type="submit" loading={isSubmitting} className="rounded-full">
                  {PASSWORD_RECOVERY_COPY.resetSubmitCta}
                </PrimaryButton>
                <button
                  type="button"
                  onClick={handleBackToOtp}
                  className="w-full text-center text-sm font-semibold text-primary transition hover:underline"
                >
                  {mode === 'first-login' ? 'Back to Login' : 'Back to OTP Verification'}
                </button>
              </div>
            </form>

            <div className="mt-6 border-t border-surface-variant/30 pt-4 text-xs text-on-surface-variant">
              Password reset account: <span className="font-semibold text-on-surface">{email || 'Unknown user'}</span>
            </div>
          </section>
        </div>
      </main>

      <AuthRecoveryFooter />
    </div>
  )
}
